/**
 * @deprecated A new environment manager has been created that works for both SSR and CSR.
 * Refer to the bottom portion of this file (below the 🚨 line) for the new API and helpers.
 */
class Environment {
  public static get primary() {
    if (Environment.devOrLoc) return "dev";
    if (Environment.subdomain?.includes("test")) return Environment.subdomain;

    return "prod";
  }

  public static get subdomain() {
    if (window.location?.hostname === "localhost") return "localhost";
    if (window.location?.hostname?.search(/\b.coder.\b/) > -1) return "coder";

    const [, subdomain] = window.location?.hostname.split(".") ?? [];
    return subdomain;
  }

  /**
   * Lets you know whether your in dev or loc. If your not in either it returns false.
   */
  public static get devOrLoc() {
    // Use indexOf here because this file is used by raven-js before polyfills are loaded.

    if (
      ["localhost", "loc", "dev", "coder"].indexOf(Environment.subdomain!) >= 0
    )
      return Environment.subdomain as "loc" | "dev";

    return false;
  }

  /**
   * Returns the current domain with dev or test subdomains added depending on environment.
   * This returns the domain without www. This also coerces loc to dev.
   * Examples: www.loc.webstaurantstore.com -> dev.webstaurantstore.com
   * www.dev.webstaurantstore.com -> dev.webstaurantstore.com
   * www.webstaurantstore.com -> webstaurantstore.com
   */
  public static get domainWithEnvironment() {
    let environment = "";

    if (Environment.devOrLoc) environment = "dev.";

    if (Environment.subdomain?.includes("test")) environment = "test.";

    return `${environment}webstaurantstore.com`;
  }

  public static get stashDomainWithEnvironment() {
    const subdomain = Environment.subdomain;
    let stashEndpoint = "stash.webstaurantstore.com";

    if (
      (subdomain && subdomain?.search(/\bdev?(\d+)?\b/) > -1) ||
      (subdomain && subdomain?.search(/\bloc?(\d+)?\b/) > -1) ||
      (subdomain && subdomain === "coder") ||
      (subdomain && subdomain === "localhost")
    ) {
      stashEndpoint = "stash.dev.webstaurantstore.com";
    } else if (
      (subdomain && subdomain?.search(/\btest?(\d+)?\b/) > -1) ||
      (subdomain && subdomain?.search(/\bpreflight?(\d+)?\b/) > -1)
    ) {
      stashEndpoint = "stash.test.webstaurantstore.com";
    }

    return stashEndpoint;
  }

  public static get isProduction() {
    return ENV === "production";
  }
}

export default Environment;

//------------------------------------------------------------------------------------------------------------
// 🚨 Everything above this line is deprecated. Use the new environment helpers and utilities below.
//------------------------------------------------------------------------------------------------------------

type SubDomains = "local" | "development" | "test" | "production";
const hostname = window?.location?.hostname ?? "";
const [, subdomain] = hostname.split(".");
const isLoc = ENV === "local";
const isDev = ENV === "development";
const isTest = subdomain?.includes("test");
const isPreflight = hostname.includes("preflight");
const isProd = !isLoc && !isDev && !isTest;
const isCoder = hostname.includes("coder");
const isLocalhost = hostname === "localhost";

function getEnv(): SubDomains {
  if (isLoc) {
    return "local";
  }

  if (isDev) {
    return "development";
  }

  if (isTest) {
    return "test";
  }

  return "production";
}

const current = getEnv();

export const env = {
  current,
  isLoc,
  isDev,
  isTest,
  isProd,
  isPreflight,
  isCoder,
  isLocalhost,
};

export const getStashDomainWithEnvironment = (): string => {
  if (env.isDev || env.isLoc || env.isCoder || env.isLocalhost) {
    return "stash.dev.webstaurantstore.com";
  }
  if (env.isTest || env.isPreflight) {
    return "stash.test.webstaurantstore.com";
  }
  return "stash.webstaurantstore.com";
};
